import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Instagram, Facebook, Twitter, MapPin, Phone, Mail, X } from 'lucide-react';
import { Link } from 'react-router-dom';
type ModalType = 'privacy' | 'terms' | null;
function LegalModal({
  type,
  onClose
}: {
  type: 'privacy' | 'terms';
  onClose: () => void;
}) {
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2
  }} className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={onClose} data-id="element-14">
      <div className="absolute inset-0 bg-cafe-900/70 backdrop-blur-sm" data-id="element-15" />
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} transition={{
      duration: 0.3,
      ease: [0.22, 1, 0.36, 1]
    }} className="relative bg-cafe-100 text-cafe-900 rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-16">
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-5 border-b border-cafe-800/10 bg-cafe-50 sticky top-0 z-10" data-id="element-17">
          <h2 className="font-serif text-2xl font-bold" data-id="element-18">
            {type === 'privacy' ? 'Privacy Policy' : 'Terms of Service'}
          </h2>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-cafe-800/10 flex items-center justify-center hover:bg-cafe-800/20 transition-colors" data-id="element-19">
            <X className="w-5 h-5" data-id="element-20" />
          </button>
        </div>

        {/* Content */}
        <div className="px-8 py-6 overflow-y-auto max-h-[calc(80vh-72px)] text-sm leading-relaxed text-cafe-800/90 space-y-6" data-id="element-21">
          {type === 'privacy' ? <>
              <p className="text-cafe-800/60 text-xs uppercase tracking-wider" data-id="element-22">
                Last updated: May 1, 2026
              </p>

              <section className="space-y-3" data-id="element-23">
                <h3 className="font-serif text-lg font-bold text-cafe-900" data-id="element-24">
                  1. Information We Collect
                </h3>
                <p data-id="element-25">
                  At Brew & Bloom Café, we value your privacy. When you visit
                  our website or café, we may collect the following information:
                </p>
                <ul className="list-disc pl-5 space-y-1" data-id="element-26">
                  <li data-id="element-27">
                    <strong data-id="element-28">Personal Information:</strong> Name, email address,
                    and phone number when you fill out our contact form or
                    subscribe to our newsletter.
                  </li>
                  <li data-id="element-29">
                    <strong data-id="element-30">Usage Data:</strong> Information about how you
                    interact with our website, including pages visited, time
                    spent, and browser type.
                  </li>
                  <li data-id="element-31">
                    <strong data-id="element-32">Cookies:</strong> We use cookies to improve your
                    browsing experience and analyze website traffic.
                  </li>
                </ul>
              </section>

              <section className="space-y-3" data-id="element-33">
                <h3 className="font-serif text-lg font-bold text-cafe-900" data-id="element-34">
                  2. How We Use Your Information
                </h3>
                <p data-id="element-35">We use the information we collect to:</p>
                <ul className="list-disc pl-5 space-y-1" data-id="element-36">
                  <li data-id="element-37">
                    Respond to your inquiries and provide customer support
                  </li>
                  <li data-id="element-38">
                    Send you newsletters and promotional materials (with your
                    consent)
                  </li>
                  <li data-id="element-39">Improve our website and services</li>
                  <li data-id="element-40">
                    Analyze trends and user behavior to enhance the customer
                    experience
                  </li>
                </ul>
              </section>

              <section className="space-y-3" data-id="element-41">
                <h3 className="font-serif text-lg font-bold text-cafe-900" data-id="element-42">
                  3. Data Sharing
                </h3>
                <p data-id="element-43">
                  We do not sell, trade, or rent your personal information to
                  third parties. We may share your data with trusted service
                  providers who assist us in operating our website, conducting
                  our business, or serving you, so long as those parties agree
                  to keep this information confidential.
                </p>
              </section>

              <section className="space-y-3" data-id="element-44">
                <h3 className="font-serif text-lg font-bold text-cafe-900" data-id="element-45">
                  4. Data Security
                </h3>
                <p data-id="element-46">
                  We implement a variety of security measures to maintain the
                  safety of your personal information. However, no method of
                  transmission over the Internet or electronic storage is 100%
                  secure, and we cannot guarantee absolute security.
                </p>
              </section>

              <section className="space-y-3" data-id="element-47">
                <h3 className="font-serif text-lg font-bold text-cafe-900" data-id="element-48">
                  5. Your Rights
                </h3>
                <p data-id="element-49">You have the right to:</p>
                <ul className="list-disc pl-5 space-y-1" data-id="element-50">
                  <li data-id="element-51">Access the personal data we hold about you</li>
                  <li data-id="element-52">Request correction of inaccurate data</li>
                  <li data-id="element-53">Request deletion of your data</li>
                  <li data-id="element-54">Unsubscribe from our newsletter at any time</li>
                </ul>
              </section>

              <section className="space-y-3" data-id="element-55">
                <h3 className="font-serif text-lg font-bold text-cafe-900" data-id="element-56">
                  6. Contact Us
                </h3>
                <p data-id="element-57">
                  If you have any questions about this Privacy Policy, please
                  contact us at <strong data-id="element-58">hello@brewandbloom.com</strong> or call
                  us at <strong data-id="element-59">(555) 123-4567</strong>.
                </p>
              </section>
            </> : <>
              <p className="text-cafe-800/60 text-xs uppercase tracking-wider" data-id="element-60">
                Last updated: May 1, 2026
              </p>

              <section className="space-y-3" data-id="element-61">
                <h3 className="font-serif text-lg font-bold text-cafe-900" data-id="element-62">
                  1. Acceptance of Terms
                </h3>
                <p data-id="element-63">
                  By accessing and using the Brew & Bloom Café website, you
                  accept and agree to be bound by these Terms of Service. If you
                  do not agree to these terms, please do not use our website.
                </p>
              </section>

              <section className="space-y-3" data-id="element-64">
                <h3 className="font-serif text-lg font-bold text-cafe-900" data-id="element-65">
                  2. Use of Website
                </h3>
                <p data-id="element-66">
                  This website is provided for informational purposes about Brew
                  & Bloom Café, including our menu, location, and services. You
                  agree to use this website only for lawful purposes and in a
                  way that does not infringe the rights of others.
                </p>
              </section>

              <section className="space-y-3" data-id="element-67">
                <h3 className="font-serif text-lg font-bold text-cafe-900" data-id="element-68">
                  3. Intellectual Property
                </h3>
                <p data-id="element-69">
                  All content on this website, including text, graphics, logos,
                  images, and software, is the property of Brew & Bloom Café and
                  is protected by copyright and intellectual property laws. You
                  may not reproduce, distribute, or create derivative works
                  without our prior written consent.
                </p>
              </section>

              <section className="space-y-3" data-id="element-70">
                <h3 className="font-serif text-lg font-bold text-cafe-900" data-id="element-71">
                  4. Menu & Pricing
                </h3>
                <p data-id="element-72">
                  While we strive to keep our online menu accurate and up to
                  date, prices and availability are subject to change without
                  notice. The in-store menu and pricing will always take
                  precedence over the website.
                </p>
              </section>

              <section className="space-y-3" data-id="element-73">
                <h3 className="font-serif text-lg font-bold text-cafe-900" data-id="element-74">
                  5. Limitation of Liability
                </h3>
                <p data-id="element-75">
                  Brew & Bloom Café shall not be liable for any direct,
                  indirect, incidental, or consequential damages arising from
                  your use of this website. We make no warranties or
                  representations about the accuracy or completeness of the
                  content on this site.
                </p>
              </section>

              <section className="space-y-3" data-id="element-76">
                <h3 className="font-serif text-lg font-bold text-cafe-900" data-id="element-77">
                  6. Allergen Disclaimer
                </h3>
                <p data-id="element-78">
                  Our food and beverages may contain or come into contact with
                  common allergens, including but not limited to nuts, dairy,
                  gluten, soy, and eggs. Please inform our staff of any
                  allergies before ordering. We cannot guarantee a completely
                  allergen-free environment.
                </p>
              </section>

              <section className="space-y-3" data-id="element-79">
                <h3 className="font-serif text-lg font-bold text-cafe-900" data-id="element-80">
                  7. Changes to Terms
                </h3>
                <p data-id="element-81">
                  We reserve the right to modify these Terms of Service at any
                  time. Changes will be effective immediately upon posting to
                  the website. Your continued use of the website constitutes
                  acceptance of the updated terms.
                </p>
              </section>

              <section className="space-y-3" data-id="element-82">
                <h3 className="font-serif text-lg font-bold text-cafe-900" data-id="element-83">
                  8. Contact Us
                </h3>
                <p data-id="element-84">
                  If you have any questions about these Terms of Service, please
                  contact us at <strong data-id="element-85">hello@brewandbloom.com</strong> or visit
                  us at{' '}
                  <strong data-id="element-86">123 Artisan Avenue, Coffee District, CD 10012</strong>
                  .
                </p>
              </section>
            </>}
        </div>
      </motion.div>
    </motion.div>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-cafe-900 text-cafe-100 pt-16 pb-8 relative z-20" data-id="element-87">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-88">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12" data-id="element-89">
            <div className="md:col-span-1" data-id="element-90">
              <h3 className="font-serif text-2xl font-bold mb-4" data-id="element-91">
                Brew & Bloom
              </h3>
              <p className="text-cafe-100/70 text-sm leading-relaxed mb-6" data-id="element-92">
                Artisanal coffee and crafted moments in the heart of the city.
                Where every cup tells a story.
              </p>
              <div className="flex gap-4" data-id="element-93">
                <a href="#" className="w-10 h-10 rounded-full bg-cafe-800 flex items-center justify-center hover:bg-cafe-sage transition-colors" data-id="element-94">
                  <Instagram className="w-5 h-5" data-id="element-95" />
                </a>
                <a href="#" className="w-10 h-10 rounded-full bg-cafe-800 flex items-center justify-center hover:bg-cafe-sage transition-colors" data-id="element-96">
                  <Facebook className="w-5 h-5" data-id="element-97" />
                </a>
                <a href="#" className="w-10 h-10 rounded-full bg-cafe-800 flex items-center justify-center hover:bg-cafe-sage transition-colors" data-id="element-98">
                  <Twitter className="w-5 h-5" data-id="element-99" />
                </a>
              </div>
            </div>

            <div data-id="element-100">
              <h4 className="font-serif text-lg font-semibold mb-4 text-cafe-sage" data-id="element-101">
                Quick Links
              </h4>
              <ul className="space-y-3 text-sm text-cafe-100/80" data-id="element-102">
                <li data-id="element-103">
                  <Link to="/" className="hover:text-white transition-colors" data-id="element-104">
                    Home
                  </Link>
                </li>
                <li data-id="element-105">
                  <Link to="/about" className="hover:text-white transition-colors" data-id="element-106">
                    Our Story
                  </Link>
                </li>
                <li data-id="element-107">
                  <Link to="/menu" className="hover:text-white transition-colors" data-id="element-108">
                    Menu
                  </Link>
                </li>
                <li data-id="element-109">
                  <Link to="/contact" className="hover:text-white transition-colors" data-id="element-110">
                    Contact
                  </Link>
                </li>
              </ul>
            </div>

            <div data-id="element-111">
              <h4 className="font-serif text-lg font-semibold mb-4 text-cafe-sage" data-id="element-112">
                Visit Us
              </h4>
              <ul className="space-y-4 text-sm text-cafe-100/80" data-id="element-113">
                <li className="flex items-start gap-3" data-id="element-114">
                  <MapPin className="w-5 h-5 text-cafe-sage shrink-0" data-id="element-115" />
                  <span data-id="element-116">
                    123 Artisan Avenue
                    <br data-id="element-117" />
                    Coffee District, CD 10012
                  </span>
                </li>
                <li className="flex items-center gap-3" data-id="element-118">
                  <Phone className="w-5 h-5 text-cafe-sage shrink-0" data-id="element-119" />
                  <span data-id="element-120">(555) 123-4567</span>
                </li>
                <li className="flex items-center gap-3" data-id="element-121">
                  <Mail className="w-5 h-5 text-cafe-sage shrink-0" data-id="element-122" />
                  <span data-id="element-123">hello@brewandbloom.com</span>
                </li>
              </ul>
            </div>

            <div data-id="element-124">
              <h4 className="font-serif text-lg font-semibold mb-4 text-cafe-sage" data-id="element-125">
                Newsletter
              </h4>
              <p className="text-sm text-cafe-100/70 mb-4" data-id="element-126">
                Subscribe to get updates on seasonal menus and events.
              </p>
              <form className="flex flex-col gap-3" onSubmit={e => e.preventDefault()} data-id="element-127">
                <input type="email" placeholder="Your email address" className="bg-cafe-800 border border-cafe-800/50 text-cafe-100 px-4 py-2 rounded focus:outline-none focus:border-cafe-sage transition-colors" data-id="element-128" />
                <button className="bg-cafe-sage text-cafe-900 font-medium px-4 py-2 rounded hover:bg-cafe-100 transition-colors" data-id="element-129">
                  Subscribe
                </button>
              </form>
            </div>
          </div>

          <div className="border-t border-cafe-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-cafe-100/50" data-id="element-130">
            <p data-id="element-131">
              &copy; {new Date().getFullYear()} Brew & Bloom Café. All rights
              reserved.
            </p>
            <div className="flex gap-4" data-id="element-132">
              <button onClick={() => setActiveModal('privacy')} className="hover:text-cafe-100 transition-colors cursor-pointer" data-id="element-133">
                Privacy Policy
              </button>
              <button onClick={() => setActiveModal('terms')} className="hover:text-cafe-100 transition-colors cursor-pointer" data-id="element-134">
                Terms of Service
              </button>
            </div>
          </div>
        </div>
      </footer>

      <AnimatePresence data-id="element-135">
        {activeModal && <LegalModal type={activeModal} onClose={() => setActiveModal(null)} data-id="element-136" />}
      </AnimatePresence>
    </>;
}